import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Container } from "reactstrap";
import { RefreshCw } from "react-feather";
import { useNavigate } from "react-router-dom";
import Breadcrumb from "../common/breadcrumb";
import { getMerchantListing } from "../../utils/apiCalls";
import { listMoyasarMerchants, linkMoyasarMerchant, STATUS_INFO, statusLabel, SIGNATURE_LABELS } from "../../utils/moyasarPlatformApi";
import { successToast, errorToast } from "../../shared/Toast";
import { C, tbl, reportingGlobalCSS } from "../reporting/reportingStyles";
import { RCard, Loader as RLoader } from "../reporting/ReportingShared";

// Every merchant we have onboarded (or are onboarding) on Moyasar Platform, plus the Moyasar
// sub-merchants nobody has linked to a merchant yet — e.g. ones created by hand before this
// page existed. Click a row to open the merchant's onboarding page.

export const STATUS_COLOR = {
  creating: { bg: C.orangeLight, fg: C.orange },
  create_failed: { bg: C.coralLight, fg: "#C0392B" },
  pending: { bg: C.blueLight, fg: C.blue },
  under_review: { bg: C.purpleLight, fg: C.purple },
  missing_docs: { bg: "#FDEDEC", fg: "#C0392B" },
  semi_active: { bg: C.orangeLight, fg: C.orange },
  active: { bg: C.greenLight, fg: "#1E8449" },
  hold: { bg: "#FDEDEC", fg: "#C0392B" },
  rejected: { bg: "#FDEDEC", fg: "#C0392B" },
  disabled: { bg: C.bg, fg: C.textMed },
};

export const StatusPill = ({ status }) => {
  const c = STATUS_COLOR[status] || { bg: C.bg, fg: C.textMed };
  return (
    <span
      title={STATUS_INFO[status]?.help || "A status Moyasar added that this page does not know yet."}
      style={{ display: "inline-block", padding: "3px 10px", borderRadius: 999, fontSize: 11, fontWeight: 700, background: c.bg, color: c.fg, whiteSpace: "nowrap" }}
    >
      {statusLabel(status)}
    </span>
  );
};

const formatDate = (d) => (d ? new Date(d).toLocaleString() : "—");

const smallSelect = { padding: "6px 10px", fontSize: 12, borderRadius: 8, border: `1.5px solid ${C.border}`, background: C.white, maxWidth: 220 };
const btn = (primary) => ({
  padding: "7px 14px",
  borderRadius: 8,
  border: `1.5px solid ${primary ? C.coral : C.border}`,
  background: primary ? C.coral : C.white,
  color: primary ? C.white : C.text,
  fontSize: 12,
  fontWeight: 600,
  cursor: "pointer",
});

const MoyasarMerchants = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [merchantList, setMerchantList] = useState([]);
  const [linkChoice, setLinkChoice] = useState({});
  const [linking, setLinking] = useState(null);
  const [onboardChoice, setOnboardChoice] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    const res = await listMoyasarMerchants();
    if (res.ok) {
      setData(res.data);
      setError(null);
    } else {
      setError(res.error.message);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    (async () => setMerchantList((await getMerchantListing()) || []))();
  }, [load]);

  // Merchants that already have an onboarding cannot be picked as a link target.
  const linkable = useMemo(() => {
    const taken = new Set((data?.merchants || []).map((m) => m.merchantId));
    return merchantList.filter((m) => !taken.has(m.id));
  }, [data, merchantList]);

  const link = async (moyasarMerchantId) => {
    const merchantId = linkChoice[moyasarMerchantId];
    if (!merchantId) return errorToast("Choose which merchant this Moyasar account belongs to");
    setLinking(moyasarMerchantId);
    const res = await linkMoyasarMerchant(merchantId, moyasarMerchantId);
    setLinking(null);
    if (!res.ok) return errorToast(res.error.message);
    successToast("Linked");
    return load();
  };

  return (
    <Fragment>
      <Breadcrumb title="Moyasar Platform" parent="Merchants" />
      <Container fluid={true}>
        <style>{reportingGlobalCSS}</style>

        {data && !data.configured && (
          <RCard>
            <div style={{ color: "#C0392B", fontWeight: 600 }}>
              Moyasar Platform is not configured on this server. Set MOYASAR_PLATFORM_API_KEY and MOYASAR_PLATFORM_BASE_URL in the API's .env.
            </div>
          </RCard>
        )}

        {data?.configured && (
          <RCard title="Onboard a merchant">
            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <select style={{ ...smallSelect, maxWidth: 320 }} value={onboardChoice} onChange={(e) => setOnboardChoice(e.target.value)}>
                <option value="">Choose a merchant not yet on Moyasar…</option>
                {linkable.map((m) => (
                  <option key={m.id} value={m.id}>{m.name}</option>
                ))}
              </select>
              <button
                style={btn(true)}
                disabled={!onboardChoice}
                onClick={() => navigate(`/merchant/moyasar-platform/${onboardChoice}`)}
              >
                Start onboarding
              </button>
              <span style={{ fontSize: 12, color: C.textMed }}>Opens the create form: details, fees, then documents.</span>
            </div>
          </RCard>
        )}

        <RCard
          title={
            <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
              <span>Merchants on Moyasar</span>
              <span style={{ marginLeft: 12, fontSize: 11, fontWeight: 500, color: C.textMed }}>
                {data?.platformId ? `Platform ${data.platformId}` : ""}
              </span>
              <button style={{ ...btn(false), marginLeft: "auto", display: "flex", alignItems: "center", gap: 6 }} onClick={load}>
                <RefreshCw size={13} /> Refresh
              </button>
            </div>
          }
        >
          {loading ? (
            <RLoader />
          ) : error ? (
            <div style={{ color: "#C0392B" }}>{error}</div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={tbl.table}>
                <thead>
                  <tr>
                    <th style={tbl.th}>Merchant</th>
                    <th style={tbl.th}>Legal name (on Moyasar)</th>
                    <th style={tbl.th}>Type</th>
                    <th style={tbl.th}>Status</th>
                    <th style={tbl.th}>Documents</th>
                    <th style={tbl.th}>Contract (Nafath)</th>
                    <th style={tbl.th}>Last synced</th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.merchants || []).length === 0 ? (
                    <tr>
                      <td style={{ ...tbl.td, textAlign: "center", color: C.textMed }} colSpan={7}>
                        No merchant is onboarded yet. Pick one under "Onboard a merchant" above, or link an existing Moyasar merchant below.
                      </td>
                    </tr>
                  ) : (
                    data.merchants.map((m) => (
                      <tr key={m.id} className="rpt-row" style={{ cursor: "pointer" }} onClick={() => navigate(`/merchant/moyasar-platform/${m.merchantId}`)}>
                        <td style={{ ...tbl.td, fontWeight: 700, color: C.coral }}>
                          {m.merchantName || `#${m.merchantId}`}
                          {m.otherPlatform && <div style={{ fontSize: 10, color: C.orange, fontWeight: 600 }}>other platform (read-only)</div>}
                        </td>
                        <td style={tbl.td} dir="auto">{m.legalName || "—"}</td>
                        <td style={{ ...tbl.td, textTransform: "capitalize" }}>{m.type || "—"}</td>
                        <td style={tbl.td}><StatusPill status={m.status} /></td>
                        <td style={tbl.td}>{m.documentsComplete ? "✓ Complete" : `${m.documentsSummary.length} uploaded`}</td>
                        <td style={tbl.td}>{SIGNATURE_LABELS[m.signatureStatus] || m.signatureStatus || "—"}</td>
                        <td style={{ ...tbl.td, fontSize: 12, color: C.textMed }}>{formatDate(m.lastSyncedAt)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </RCard>

        {!loading && !error && data?.configured && (
          <RCard title="On Moyasar but not linked to a merchant">
            {data.remoteError && <div style={{ color: "#C0392B", marginBottom: 10 }}>{data.remoteError}</div>}
            {(data.unlinked || []).length === 0 ? (
              <div style={{ color: C.textMed, fontSize: 13 }}>Every Moyasar merchant on this platform is linked.</div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={tbl.table}>
                  <thead>
                    <tr>
                      <th style={tbl.th}>Moyasar merchant</th>
                      <th style={tbl.th}>Status</th>
                      <th style={tbl.th}>Email</th>
                      <th style={tbl.th}>Created</th>
                      <th style={tbl.th}>Link to</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.unlinked.map((u) => (
                      <tr key={u.moyasarMerchantId}>
                        <td style={tbl.td}>
                          <div style={{ fontWeight: 600 }} dir="auto">{u.publicName || u.name}</div>
                          <div style={{ fontSize: 11, color: C.textMed }} dir="auto">{u.publicName ? u.name : ""}</div>
                          <div style={{ fontSize: 10, color: C.textLight, fontFamily: "monospace" }}>{u.moyasarMerchantId}</div>
                        </td>
                        <td style={tbl.td}><StatusPill status={u.status} /></td>
                        <td style={{ ...tbl.td, fontSize: 12 }}>{u.email || "—"}</td>
                        <td style={{ ...tbl.td, fontSize: 12, color: C.textMed }}>{formatDate(u.createdAt)}</td>
                        <td style={tbl.td}>
                          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                            <select
                              style={smallSelect}
                              value={linkChoice[u.moyasarMerchantId] || ""}
                              onChange={(e) => setLinkChoice({ ...linkChoice, [u.moyasarMerchantId]: e.target.value })}
                            >
                              <option value="">Choose merchant…</option>
                              {linkable.map((m) => (
                                <option key={m.id} value={m.id}>{m.name}</option>
                              ))}
                            </select>
                            <button style={btn(true)} disabled={linking === u.moyasarMerchantId} onClick={() => link(u.moyasarMerchantId)}>
                              {linking === u.moyasarMerchantId ? "Linking…" : "Link"}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </RCard>
        )}
      </Container>
    </Fragment>
  );
};

export default MoyasarMerchants;
